import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { format, formatDistanceStrict } from 'date-fns'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts'
import { marketAPI } from '../services/api'
import type { CandleSeries, ChartTimeframe } from '../types'
import { formatAxisPrice, formatPrice } from '../utils/marketPrice'
import TradingViewChart from './TradingViewChart'
import { ChartDirection, niceScale, useChartTheme } from './dashboard/chartTheme'
import { displaySymbol } from '../utils/displaySymbol'

// Faster than the shortest server TTL (20s for 1m) would only re-read the
// cached series; slower stops feeling live.
export const CHART_POLL_INTERVAL_MS = 25_000

const DEFAULT_SYMBOL = 'BTC/USDT'
const DEFAULT_TIMEFRAME: ChartTimeframe = '1h'
const TIMEFRAMES: ChartTimeframe[] = ['1m', '5m', '1h', '1d']
// Simple is our own chart; Advanced swaps in TradingView's widget for the same coin.
const CHART_VIEWS = ['simple', 'advanced'] as const
type ChartView = (typeof CHART_VIEWS)[number]

const TICK_FORMAT: Record<ChartTimeframe, string> = { '1m': 'HH:mm', '5m': 'HH:mm', '1h': 'MMM d', '1d': 'MMM' }
const TOOLTIP_FORMAT: Record<ChartTimeframe, string> = {
  '1m': 'MMM d, HH:mm',
  '5m': 'MMM d, HH:mm',
  '1h': 'MMM d, HH:mm',
  '1d': 'MMM d, yyyy',
}

// Direction is always spelled out as ▲/▼ plus a signed percent beside the line.
type Direction = ChartDirection

interface ChartPoint {
  time: number
  close: number
}

const directionOf = (change: number | null): Direction =>
  change == null || change === 0 ? 'flat' : change > 0 ? 'up' : 'down'

const formatChange = (change: number | null): string => {
  if (change == null) return '—'
  const arrow = change > 0 ? '▲' : change < 0 ? '▼' : ''
  return `${arrow} ${change > 0 ? '+' : ''}${change.toFixed(2)}%`.trim()
}

/**
 * Calendar-aligned time ticks: each local midnight on 1h, each month start on
 * 1d. Recharts' own ticks fall every ~12h, so a "MMM d" label repeats. 1m/5m
 * label the clock and use Recharts' spacing; so does a window too short to
 * hold two boundaries.
 */
const calendarTicks = (points: ChartPoint[], timeframe: ChartTimeframe): number[] | undefined => {
  if (points.length < 2 || (timeframe !== '1h' && timeframe !== '1d')) return undefined
  const last = points[points.length - 1].time
  const cursor = new Date(points[0].time)
  cursor.setHours(0, 0, 0, 0)
  const advance = () => (timeframe === '1h' ? cursor.setDate(cursor.getDate() + 1) : cursor.setMonth(cursor.getMonth() + 1))
  if (timeframe === '1d') cursor.setDate(1)
  advance()
  const ticks: number[] = []
  while (cursor.getTime() <= last) {
    ticks.push(cursor.getTime())
    advance()
  }
  return ticks.length >= 2 ? ticks : undefined
}

interface PriceTooltipProps {
  active?: boolean
  payload?: Array<{ payload: ChartPoint }>
  timeframe: ChartTimeframe
}

// Value leads, time follows: the reader already knows which coin this is.
const PriceTooltip = ({ active, payload, timeframe }: PriceTooltipProps) => {
  if (!active || !payload?.length) return null
  const point = payload[0].payload
  return (
    <div className="rounded-md border border-terminal-border bg-terminal-raised px-3 py-2 shadow-lg">
      <p className="text-sm font-semibold tabular-nums text-gray-50">${formatPrice(point.close)}</p>
      <p className="text-xs text-gray-400">{format(point.time, TOOLTIP_FORMAT[timeframe])}</p>
    </div>
  )
}

/**
 * The Dashboard's live price chart: close prices from the signal engine's
 * venue, for any symbol in the ticker universe, refreshed every 25 seconds.
 * The line is green when the window is up and red when it is down. Styled for
 * the Dashboard's dark terminal theme only.
 *
 * A Simple/Advanced toggle swaps this chart for TradingView's embedded widget
 * on the selected coin. TradingView brings its own price feed, which can
 * differ slightly from ours, so polling pauses while it is shown.
 */
const LivePriceChart = () => {
  const chart = useChartTheme()
  const [symbol, setSymbol] = useState(DEFAULT_SYMBOL)
  const [timeframe, setTimeframe] = useState<ChartTimeframe>(DEFAULT_TIMEFRAME)
  const [series, setSeries] = useState<CandleSeries | null>(null)
  const [symbols, setSymbols] = useState<string[]>([DEFAULT_SYMBOL])
  const [failed, setFailed] = useState(false)
  const [view, setView] = useState<ChartView>('simple')
  const latestRequest = useRef(0)
  const gradientId = `live-price-fill-${useId().replace(/:/g, '')}`

  useEffect(() => {
    if (view === 'advanced') return
    let active = true

    const load = async () => {
      // A background tab needn't poll; the next visible poll catches up.
      if (document.hidden && latestRequest.current > 0) return
      const requestId = ++latestRequest.current
      try {
        const data = await marketAPI.getCandles(symbol, timeframe)
        if (!active || requestId !== latestRequest.current) return
        setSeries(data)
        setSymbols(data.symbols)
        setFailed(false)
      } catch (error) {
        if (!active || requestId !== latestRequest.current) return
        // Keep the last frame; the backend already serves its last good series
        // when the venue is down, so this is a network blip or a bad selection.
        console.error('Price chart refresh failed:', error)
        setFailed(true)
      }
    }

    load()
    const timer = setInterval(load, CHART_POLL_INTERVAL_MS)
    return () => {
      active = false
      clearInterval(timer)
    }
  }, [symbol, timeframe, view])

  const points = useMemo<ChartPoint[]>(
    () => (series?.points ?? []).map((p) => ({ time: Date.parse(p.timestamp), close: p.close })),
    [series]
  )
  const yScale = useMemo(() => niceScale(points.map((p) => p.close)), [points])
  const xTicks = useMemo(() => (series ? calendarTicks(points, series.timeframe) : undefined), [points, series])

  // The frame on screen belongs to an earlier selection until the new fetch lands.
  const isCurrent = series != null && series.symbol === symbol && series.timeframe === timeframe
  const direction = directionOf(series?.change_pct ?? null)
  const color = chart.colors[direction]
  const window =
    points.length >= 2 ? formatDistanceStrict(points[0].time, points[points.length - 1].time) : null
  const sourceName = series ? `${series.source.charAt(0).toUpperCase()}${series.source.slice(1)}` : null

  const changeClass =
    direction === 'up' ? 'text-green-400 bg-green-500/10' : direction === 'down' ? 'text-red-400 bg-red-500/10' : 'text-gray-300 bg-white/5'

  return (
    <section
      aria-labelledby="live-price-heading"
      className="rounded-lg border border-terminal-border bg-terminal-surface p-4 md:p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <h2 id="live-price-heading" className="text-xs font-medium uppercase tracking-wider text-gray-400">
              Live price
            </h2>
            <select
              aria-label="Chart coin"
              value={symbol}
              onChange={(event) => setSymbol(event.target.value)}
              className="rounded-md border border-terminal-border bg-terminal-raised py-1 pl-2 pr-8 text-sm font-semibold text-gray-100 [color-scheme:dark] focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {(symbols.includes(symbol) ? symbols : [symbol, ...symbols]).map((option) => (
                <option key={option} value={option}>
                  {displaySymbol(option)}
                </option>
              ))}
            </select>
          </div>
          {series && view === 'simple' && (
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1" data-testid="live-price-readout">
              <span className="text-3xl font-bold tabular-nums text-gray-50">${formatPrice(series.last_price)}</span>
              <span
                className={`rounded px-2 py-0.5 text-sm font-semibold tabular-nums ${changeClass}`}
                data-direction={direction}
              >
                {formatChange(series.change_pct)}
              </span>
              {window && <span className="text-xs text-gray-500">over {window}</span>}
            </div>
          )}
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {view === 'simple' && (
              <div
                role="group"
                aria-label="Chart timeframe"
                className="inline-flex rounded-md border border-terminal-border bg-terminal-raised p-0.5"
              >
                {TIMEFRAMES.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={timeframe === option}
                    onClick={() => setTimeframe(option)}
                    className={`rounded px-3 py-1 text-xs font-semibold tabular-nums ${
                      timeframe === option
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
            <div
              role="group"
              aria-label="Chart view"
              className="inline-flex rounded-md border border-terminal-border bg-terminal-raised p-0.5"
            >
              {CHART_VIEWS.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={view === option}
                  onClick={() => setView(option)}
                  className={`rounded px-3 py-1 text-xs font-semibold ${
                    view === option ? 'bg-primary-600 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                  }`}
                >
                  {option === 'simple' ? 'Simple' : 'Advanced'}
                </button>
              ))}
            </div>
          </div>
          {view === 'advanced' ? (
            <p className="text-xs text-gray-500" data-testid="live-price-status">
              TradingView prices · may differ slightly from {sourceName ?? 'our exchange'}&rsquo;s
            </p>
          ) : series && (
            <p className="flex items-center gap-1.5 text-xs text-gray-500" data-testid="live-price-status">
              <span
                aria-hidden="true"
                className={`h-1.5 w-1.5 rounded-full ${series.stale || failed ? 'bg-amber-400' : 'bg-green-400'}`}
              />
              {series.stale || (failed && isCurrent)
                ? 'Delayed — showing the last prices received'
                : `${sourceName} · refreshes every ${CHART_POLL_INTERVAL_MS / 1000}s`}
            </p>
          )}
        </div>
      </div>

      <div className={`relative mt-4 ${view === 'advanced' ? 'h-[28rem] md:h-[32rem]' : 'h-64 md:h-80'}`}>
        {view === 'advanced' ? (
          <TradingViewChart symbol={symbol} timeframe={timeframe} />
        ) : failed && !isCurrent ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">
            Price data for {displaySymbol(symbol)} is unavailable right now.
          </div>
        ) : !series ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">Loading prices…</div>
        ) : (
          <div
            role="img"
            aria-label={`${displaySymbol(series.symbol)} ${series.timeframe} price${window ? ` over ${window}` : ''}: last $${formatPrice(
              series.last_price
            )}, ${direction === 'flat' ? 'unchanged' : `${direction} ${formatChange(series.change_pct).replace(/[▲▼] /, '')}`}`}
            aria-busy={!isCurrent}
            data-direction={direction}
            data-testid="live-price-chart"
            className={`h-full transition-opacity ${isCurrent ? 'opacity-100' : 'opacity-40'}`}
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={points} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={chart.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="time"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  ticks={xTicks}
                  tickFormatter={(time: number) => format(time, TICK_FORMAT[series.timeframe])}
                  tick={chart.axisTick}
                  tickLine={false}
                  axisLine={{ stroke: chart.grid }}
                  minTickGap={40}
                />
                <YAxis
                  orientation="right"
                  domain={yScale.domain}
                  ticks={yScale.ticks}
                  tickFormatter={formatAxisPrice}
                  tick={chart.axisTick}
                  tickLine={false}
                  axisLine={false}
                  width={72}
                />
                <ChartTooltip
                  content={<PriceTooltip timeframe={series.timeframe} />}
                  cursor={{ stroke: chart.cursor, strokeWidth: 1, strokeDasharray: '3 3' }}
                  isAnimationActive={false}
                />
                <Area
                  type="linear"
                  dataKey="close"
                  stroke={color}
                  strokeWidth={2}
                  fill={`url(#${gradientId})`}
                  dot={false}
                  activeDot={{ r: 4, fill: color, stroke: chart.surface, strokeWidth: 2 }}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </section>
  )
}

export default LivePriceChart
